import {
  Component, forwardRef, OnInit, OnDestroy, Input, Output, EventEmitter,
  ChangeDetectionStrategy, ChangeDetectorRef, NgZone, AfterViewInit, ViewChild
} from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { ProductClient, ProductDto } from '../../api-connector/sso-connector';
import { AuthService } from '../../services/auth.service';
import { UploadService } from '../../services/upload.service';
import { BaseAutocompleteControl } from '../base-autocomplete-control';
import { AutocompleteCacheService } from '../../services/autocomplete-cache.service';

const PAGE_SIZE = 20;

@Component({
  selector: 'app-product-search-control',
  templateUrl: './product-search-control.component.html',
  styleUrls: ['./product-search-control.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ProductSearchControlComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ProductSearchControlComponent),
      multi: true,
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductSearchControlComponent extends BaseAutocompleteControl<ProductDto>
  implements OnInit, AfterViewInit, OnDestroy {

  @Input() isSearchBuDon: boolean = false;
  @Input() isHomepage: boolean = false;
  @Input() isShowPriceCost: boolean = false;

  @Output() selectedProduct = new EventEmitter<ProductDto>();

  // ── Scroll loading state ───────────────────────────────────────────────────
  /** Danh sách tích lũy (page 0 + page 1 + ...) */
  displayedItems: ProductDto[] = [];
  isLoadingMore = false;
  hasMore = true;

  private currentPageIndex = 0;
  private currentKeyword = '';
  private scrollPanel: Element | null = null;
  private scrollListener: (() => void) | null = null;
  private panelDestroy$ = new Subject<void>();

  @ViewChild('autoProduct') autoProduct!: MatAutocomplete;

  constructor(
    private ProductClient: ProductClient,
    private uploadService: UploadService,
    public authService: AuthService,
    private cache: AutocompleteCacheService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
  ) {
    super();
  }

  ngOnInit(): void {
    // Không gọi super.ngOnInit() vì chúng ta override toàn bộ filtering
    this.initScrollableFiltering();
    this.setupValueChanges();
  }

  ngAfterViewInit(): void {
    // Lắng nghe khi panel mở để attach scroll listener và trigger load lần đầu
    this.autoProduct.opened.pipe(takeUntil(this.destroy$)).subscribe(() => {
      // Trigger load lần đầu nếu chưa có data (tương đương startWith('') cũ)
      if (this.displayedItems.length === 0 && !this.isLoading) {
        const currentVal = this.control.value;
        const keyword = typeof currentVal === 'string' ? currentVal.trim() : '';
        this.currentKeyword = keyword;
        this.currentPageIndex = 0;
        this.hasMore = true;
        this.loadPage(0, true);
      }

      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => this.attachScrollListener(), 50);
      });
    });

    this.autoProduct.closed.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.detachScrollListener();
    });
  }

  ngOnDestroy(): void {
    this.detachScrollListener();
    super.ngOnDestroy();
  }

  // ── Filtering với scroll loading ───────────────────────────────────────────

  /**
   * Override initializeFiltering() bằng logic có pagination
   */
  protected initializeFiltering(): void {
    // Không làm gì ở đây — chúng ta dùng initScrollableFiltering() thay thế
  }

  private initScrollableFiltering(): void {
    const minLength = this.getMinSearchLength();

    this.control.valueChanges.pipe(
      debounceTime(400),
      filter(value => typeof value !== 'object' || value === null),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    ).subscribe((rawValue: any) => {
      const keyword = typeof rawValue === 'string' ? rawValue.trim() : '';

      // Keyword quá ngắn → xóa list, chờ user gõ thêm
      if (keyword.length > 0 && keyword.length < minLength) {
        this.displayedItems = [];
        this.hasMore = false;
        this.cdr.markForCheck();
        return;
      }

      // Keyword rỗng (user xóa hết / vừa clear) → reset, panel.opened sẽ load lại
      if (keyword === '' && this.currentKeyword !== '') {
        this.displayedItems = [];
        this.currentKeyword = '';
        this.currentPageIndex = 0;
        this.hasMore = true;
        this.cdr.markForCheck();
        return; // Không load ngay, chờ panel opened
      }

      // Keyword thay đổi → reset về page 0 và load mới
      this.currentKeyword = keyword;
      this.currentPageIndex = 0;
      this.hasMore = true;
      this.displayedItems = [];
      this.loadPage(0, true);
    });
  }

  /**
   * Gọi API lấy trang `page`, kết quả append vào displayedItems
   */
  private loadPage(pageIndex: number, isFirstPage: boolean): void {
    if (isFirstPage) {
      this.isLoading = true;
    } else {
      this.isLoadingMore = true;
    }
    this.cdr.markForCheck();

    const cacheKey = `products_search_${this.currentKeyword}_hp_${this.isHomepage}_bd_${this.isSearchBuDon}_p_${pageIndex}`;

    this.cache.wrap(
      cacheKey,
      this.ProductClient.getProductByKeySearch(
        this.currentKeyword,
        this.isHomepage,
        this.isSearchBuDon,
        pageIndex
      )
    ).pipe(takeUntil(this.destroy$)).subscribe({
      next: (items) => {
        const list = items || [];
        if (isFirstPage) {
          this.displayedItems = list;
          this.isLoading = false;
        } else {
          this.displayedItems = [...this.displayedItems, ...list];
          this.isLoadingMore = false;
        }

        // Nếu trả về ít hơn pageSize → không còn dữ liệu nữa
        this.hasMore = list.length >= PAGE_SIZE;
        this.cdr.markForCheck();
      },
      error: () => {
        this.isLoading = false;
        this.isLoadingMore = false;
        this.cdr.markForCheck();
      }
    });
  }

  // ── Scroll detection ───────────────────────────────────────────────────────

  private attachScrollListener(): void {
    this.detachScrollListener(); // Tránh duplicate

    // Mat-autocomplete render trong CDK overlay panel
    const panel = document.querySelector('.product-search-panel .mat-autocomplete-panel')
      ?? document.querySelector('mat-autocomplete .mat-option')?.closest('.mat-autocomplete-panel')
      ?? document.querySelector('.mat-autocomplete-panel');

    if (!panel) return;

    this.scrollPanel = panel;
    this.scrollListener = () => {
      this.ngZone.run(() => this.onPanelScroll(panel));
    };
    panel.addEventListener('scroll', this.scrollListener, { passive: true });
  }

  private detachScrollListener(): void {
    if (this.scrollPanel && this.scrollListener) {
      this.scrollPanel.removeEventListener('scroll', this.scrollListener);
      this.scrollPanel = null;
      this.scrollListener = null;
    }
  }

  private onPanelScroll(panel: Element): void {
    const threshold = 60; // px từ đáy thì load thêm
    const distanceToBottom = panel.scrollHeight - panel.scrollTop - panel.clientHeight;

    if (distanceToBottom <= threshold && this.hasMore && !this.isLoadingMore && !this.isLoading) {
      this.currentPageIndex++;
      this.loadPage(this.currentPageIndex, false);
    }
  }

  // ── Kế thừa bắt buộc ──────────────────────────────────────────────────────

  protected fetchItems(searchValue: string): any {
    // Không dùng bởi vì ta override initializeFiltering()
    return [];
  }

  getDisplayValue(item: ProductDto | null): string {
    return item?.title || '';
  }

  protected isValidItem(value: any): boolean {
    return value && typeof value === 'object' && ('id' in value || 'title' in value);
  }

  protected outputValue(): void {
    super.outputValue();
    const value = this.control.value;
    if (this.isValidItem(value)) {
      this.selectedProduct.emit(value);
    }
  }

  optionSelected(event: MatAutocompleteSelectedEvent): void {
    // handled via outputValue → valueChanges
  }

  getUrlFile(id: string): string {
    return this.uploadService.getUrlFile(id);
  }

  displayName = (product?: ProductDto) => this.getDisplayValue(product || null);
}
